import {Calendar} from '@fullcalendar/core' // eslint-disable-line import/no-extraneous-dependencies
import dayGridPlugin from '@fullcalendar/daygrid' // eslint-disable-line import/no-extraneous-dependencies
import interactionPlugin from '@fullcalendar/interaction' // eslint-disable-line import/no-extraneous-dependencies
import listPlugin from '@fullcalendar/list' // eslint-disable-line import/no-extraneous-dependencies

import {
  ensureLegendPanel,
  updateLegendContent,
  toggleLegendPanel,
} from './legend'
import {
  getEventClassNames,
  eventContent,
  eventDidMount,
} from './event_renderer'

// ID used in the scheduled partial; adjust if you renamed it again.
const CALENDAR_ID = 'js-work-items-calendar'

// ensure the global namespace exists
window.gf = window.gf || {}

/**
 * Safely destroy a FullCalendar instance and clean references.
 */
function destroyInstance(instance) {
  try {
    if (instance && typeof instance.destroy === 'function')
      instance.destroy()
  } catch (err) {
    // Defensive: don't let destroy errors crash page lifecycle
    // eslint-disable-next-line no-console
    console.error('Error destroying calendar instance', err)
  }
}

/**
 * Attach instance to both element and window.gf for convenience.
 */
function attachInstanceToElement(el, calendar) {
  if (!el || !calendar)
    return
  el.fullCalendarInstance = calendar
  // keep a single global reference (acceptable for now per your plan)
  window.gf.calendar = calendar
}

/**
 * Main initialize function (idempotent).
 * - If the element already has an instance, do a refetch and return it.
 * - If a global instance exists on a different element, destroy it first.
 * - Create, render, attach instance, and set up lightweight Turbo hooks (only once).
 */
function initialize(url) {
  const calendarElement = document.getElementById(CALENDAR_ID)
  if (!calendarElement)
    return

  // If this element already has an instance, only refetch (avoid re-init)
  if (calendarElement.fullCalendarInstance) {
    calendarElement.fullCalendarInstance.refetchEvents()
    return
  }

  // If there's an existing global calendar attached to some other element, destroy it
  if (
    window.gf.calendar
    && window.gf.calendar.el
    && window.gf.calendar.el !== calendarElement
  ) {
    destroyInstance(window.gf.calendar)
    // try to find previous element and clear its pointer if present
    try {
      const prevEl = window.gf.calendar.el
      if (prevEl && prevEl.fullCalendarInstance === window.gf.calendar)
        delete prevEl.fullCalendarInstance
    } catch (e) {
      // ignore
    }
    window.gf.calendar = null
  }

  let fetchedLegend = null

  const calendar = new Calendar(calendarElement, {
    backgroundColor: '#FFFFFF',
    dateClick: (arg) => {
      // remove any stray popovers (keeps behaviour same as before)
      $('.popover').remove()

      try {
        const directNewUrl = calendarElement.dataset.newActionUrl
        if (directNewUrl) {
          const params = new URLSearchParams({scheduled_date: arg.dateStr})
          const joiner = directNewUrl.includes('?') ? '&' : '?'
          const newActionUrl = `${directNewUrl}${joiner}${params.toString()}`
          Turbo.visit(newActionUrl, {frame: 'remote-modal'})
          return
        }

        // Fallback: infer /workspace/calendar_feed -> /actions/new
        const feedUrl = calendarElement.dataset.feedUrl || url
        const basePath = feedUrl.replace(
          /\/workspace\/calendar_feed(?:\?.*)?$/,
          '',
        )
        const newActionUrl = `${basePath}/actions/new?status=scheduled&scheduled_date=${encodeURIComponent(
          arg.dateStr,
        )}`
        Turbo.visit(newActionUrl, {frame: 'remote-modal'})
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          'Error opening new scheduled action modal from calendar',
          e,
        )
      }
    },
    dayMaxEvents: true,
    eventClick: (args) => {
      Turbo.visit(args.event.extendedProps.path, {frame: 'remote-modal'})
    },
    eventColor: '#087F97', // searching for $color-link? find me!
    eventClassNames: (arg) => getEventClassNames(arg.event),
    eventContent,
    eventDidMount,
    customButtons: {
      keyLegend: {
        text: I18n.t('work_item.icon_legend.title'),
        icon: 'legend-icon', // this is set in css
        click: () => toggleLegendPanel(calendarElement, fetchedLegend),
      },
    },
    events: (fetchInfo, successCallback, failureCallback) => {
      const params = new URLSearchParams({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      })
      fetch(`${url}?${params.toString()}`, {
        headers: {Accept: 'application/json'},
        credentials: 'same-origin',
      })
        .then((r) => {
          if (!r.ok)
            throw new Error(`HTTP ${r.status}`)
          return r.json()
        })
        .then((data) => {
          // support older behavior where server returns array
          if (Array.isArray(data)) {
            fetchedLegend = fetchedLegend || null
            successCallback(data)
            return
          }

          // expected shape: { events: [...], legend: [...] }
          fetchedLegend = data.legend || fetchedLegend
          updateLegendContent(fetchedLegend)
          successCallback(data.events || [])
        })
        .catch((err) => failureCallback(err))
    },
    height: '80vh',
    headerToolbar: {
      start: '',
      center: 'prev,title,next',
      end: 'listWeek,dayGridWeek,dayGridMonth',
    },
    footerToolbar: {
      start: 'keyLegend',
      center: '',
      end: '',
    },
    plugins: [dayGridPlugin, interactionPlugin, listPlugin],
    theme: 'bootstrap',
  })

  calendar.render()
  attachInstanceToElement(calendarElement, calendar)
  ensureLegendPanel(calendarElement, fetchedLegend)

  // Attach page-level Turbo handlers only once to avoid accumulating listeners.
  // Use a flag on window.gf to mark them attached.
  if (!window.gf.calendarTurboHandlersAttached) {
    // When any turbo frame finishes loading, ask the calendar to refetch events.
    // Optionally you can check event.target.id to restrict to workspace-frame.
    document.addEventListener('turbo:frame-load', (event) => {
      try {
        const frame = event && event.target
        if (frame && frame.id && frame.id !== 'page-frame')
          return

        const el = document.getElementById(CALENDAR_ID)
        const inst = el && el.fullCalendarInstance
        if (inst && typeof inst.refetchEvents === 'function')
          inst.refetchEvents()
      } catch (e) {
        // safe no-op
        // eslint-disable-next-line no-console
        console.error('Error refetching calendar on turbo:frame-load', e)
      }
    })

    // Before Turbo caches the page (back/forward cache), destroy the instance
    // to avoid storing live JS instances tied to DOM nodes in the cache.
    document.addEventListener('turbo:before-cache', () => {
      try {
        const el = document.getElementById(CALENDAR_ID)
        const inst = el && el.fullCalendarInstance
        if (inst) {
          destroyInstance(inst)
          if (el)
            delete el.fullCalendarInstance
          if (window.gf.calendar === inst)
            window.gf.calendar = null
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          'Error during turbo:before-cache cleanup for calendar',
          err,
        )
      }
    })

    window.gf.calendarTurboHandlersAttached = true
  }

  // no explicit return to satisfy consistent-return lint rule
}

export default {initialize}
